import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { HiFlag, HiPlus, HiEye, HiArrowPath, HiTrash, HiXMark } from 'react-icons/hi2';
import { API_BASE } from '../../config';
import { formatShortDate } from '../../utils/plan_changes';
import { EVENT_SPORT_EMOJI, EVENT_PRIORITY_CLS, eventStatus } from './eventMeta';

const STATUS_CLS = {
  pending: 'bg-indigo-50 text-indigo-700 border-indigo-200',
  applied: 'bg-green-50 text-green-700 border-green-200',
  kept: 'bg-gray-50 text-gray-600 border-gray-200',
  kept_by_coach: 'bg-emerald-50 text-emerald-700 border-emerald-200',
  none: 'bg-gray-50 text-gray-500 border-gray-200'
};

function DeleteEventDialog({ event, onCancel, onConfirm, busy, error }) {
  const { t } = useTranslation();
  const [removeWorkouts, setRemoveWorkouts] = useState(true);
  return (
    <div className="fixed inset-0 bg-gray-900/60 backdrop-blur-sm flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-3xl p-6 max-w-md w-full shadow-2xl">
        <div className="flex justify-center mb-4 bg-red-50 w-14 h-14 rounded-full items-center mx-auto">
          <HiTrash className="w-7 h-7 text-red-500" />
        </div>
        <h3 className="text-lg font-black text-center text-gray-900 mb-1">{t('events.delete_title')}</h3>
        <p className="text-sm text-gray-500 text-center mb-5">{t('events.delete_msg', { title: event.title })}</p>
        <label className="flex items-start gap-3 p-3 rounded-xl border border-gray-200 bg-gray-50 cursor-pointer mb-5">
          <input
            type="checkbox"
            checked={removeWorkouts}
            onChange={(e) => setRemoveWorkouts(e.target.checked)}
            className="mt-0.5 w-5 h-5 accent-red-600"
          />
          <span className="text-sm font-bold text-gray-700">{t('events.delete_remove_workouts')}</span>
        </label>
        {error ? <div className="mb-4 rounded-xl border border-red-200 bg-red-50 p-3 text-sm font-bold text-red-700">{error}</div> : null}
        <div className="flex justify-end gap-3">
          <button
            type="button"
            onClick={onCancel}
            disabled={busy}
            className="px-5 py-2.5 text-gray-600 font-bold hover:bg-gray-100 rounded-xl transition-colors"
          >
            {t('workout.cancel')}
          </button>
          <button
            type="button"
            onClick={() => onConfirm(removeWorkouts)}
            disabled={busy}
            className={`px-5 py-2.5 rounded-xl text-white font-bold shadow-lg transition-all ${busy ? 'bg-red-300 cursor-wait' : 'bg-red-600 hover:bg-red-700'}`}
          >
            {busy ? '…' : t('workout.delete')}
          </button>
        </div>
      </div>
    </div>
  );
}

export default function EventsPanel({ planId, events, canEdit, onAdd, onOpenOptions, onChanged, className = '' }) {
  const { t, i18n } = useTranslation();
  const [replanningId, setReplanningId] = useState(null);
  const [rowError, setRowError] = useState({ id: null, message: '' });
  const [deleting, setDeleting] = useState(null); // event
  const [deleteBusy, setDeleteBusy] = useState(false);
  const [deleteError, setDeleteError] = useState('');

  const list = (Array.isArray(events) ? events : [])
    .filter(Boolean)
    .slice()
    .sort((a, b) => String(a.start_date || '').localeCompare(String(b.start_date || '')));

  if (list.length === 0 && !canEdit) return null;

  const handleReplan = async (event) => {
    if (replanningId) return;
    setReplanningId(event.id);
    setRowError({ id: null, message: '' });
    try {
      const res = await fetch(`${API_BASE}/plans/${planId}/events/${event.id}/replan`, {
        method: 'POST',
        credentials: 'include'
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data?.error || t('events.replan_error'));
      await onChanged?.();
      onOpenOptions?.(data?.event || event);
    } catch (e) {
      setRowError({ id: event.id, message: e?.message || t('events.replan_error') });
    } finally {
      setReplanningId(null);
    }
  };

  const handleDelete = async (removeWorkouts) => {
    if (!deleting || deleteBusy) return;
    setDeleteBusy(true);
    setDeleteError('');
    try {
      const res = await fetch(
        `${API_BASE}/plans/${planId}/events/${deleting.id}?remove_workouts=${removeWorkouts ? 'true' : 'false'}`,
        { method: 'DELETE', credentials: 'include' }
      );
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data?.error || t('events.delete_error'));
      setDeleting(null);
      await onChanged?.();
    } catch (e) {
      setDeleteError(e?.message || t('events.delete_error'));
    } finally {
      setDeleteBusy(false);
    }
  };

  return (
    <div className={className}>
      <div className="flex items-center justify-between gap-3 mb-3 ml-1">
        <h2 className="text-xs font-black uppercase tracking-widest text-gray-400 flex items-center gap-1.5">
          <HiFlag className="w-4 h-4" /> {t('events.title')}
        </h2>
        {canEdit ? (
          <button
            type="button"
            onClick={onAdd}
            className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-black text-indigo-700 bg-indigo-50 border border-indigo-100 hover:bg-indigo-100 transition-colors"
          >
            <HiPlus className="w-4 h-4" /> {t('events.add_cta')}
          </button>
        ) : null}
      </div>

      {list.length === 0 ? (
        <div className="rounded-2xl border border-dashed border-gray-200 bg-white p-5 text-sm text-gray-500 leading-relaxed">
          {t('events.empty')}
        </div>
      ) : (
        <div className="space-y-2">
          {list.map((event) => {
            const status = eventStatus(event);
            const multiDay = event.end_date && event.end_date !== event.start_date;
            const totalKm = (event.days || []).reduce((acc, d) => acc + (Number(d?.distance_km) || 0), 0);
            const totalElev = (event.days || []).reduce((acc, d) => acc + (Number(d?.elevation_m) || 0), 0);
            const isReplanning = replanningId === event.id;
            return (
              <div key={event.id} className="rounded-2xl border border-gray-100 bg-white p-4 shadow-sm">
                <div className="flex items-start gap-3">
                  <div className="w-10 h-10 rounded-xl bg-gray-50 border border-gray-100 flex items-center justify-center text-xl shrink-0">
                    {EVENT_SPORT_EMOJI[event.sport] || EVENT_SPORT_EMOJI.other}
                  </div>
                  <div className="min-w-0 flex-1">
                    <div className="flex flex-wrap items-center gap-2">
                      <span className="font-black text-gray-900 leading-tight">{event.title}</span>
                      <span className={`px-2 py-0.5 rounded-full border text-[10px] font-black uppercase ${EVENT_PRIORITY_CLS[event.priority] || EVENT_PRIORITY_CLS.B}`}>
                        {t(`events.priority.${event.priority || 'B'}.short`)}
                      </span>
                    </div>
                    <div className="text-xs font-bold text-gray-500 mt-1 flex flex-wrap items-center gap-x-2 gap-y-1">
                      <span>
                        {formatShortDate(event.start_date, i18n.language)}
                        {multiDay ? ` → ${formatShortDate(event.end_date, i18n.language)}` : ''}
                      </span>
                      {totalKm > 0 ? <span className="tabular-nums">· {Math.round(totalKm * 10) / 10} km</span> : null}
                      {totalElev > 0 ? <span className="tabular-nums">· +{Math.round(totalElev)} m</span> : null}
                      {event.intent ? <span>· {t(`events.intent.${event.intent}`)}</span> : null}
                    </div>
                    <div className="mt-2">
                      <span className={`inline-flex px-2 py-0.5 rounded-full border text-[10px] font-black uppercase tracking-wide ${STATUS_CLS[status]}`}>
                        {isReplanning ? t('events.status.replanning') : t(`events.status.${status}`)}
                      </span>
                    </div>
                  </div>
                </div>

                {isReplanning ? (
                  <div className="mt-3 rounded-xl border border-indigo-100 bg-indigo-50 p-3 flex items-center gap-2 text-xs font-bold text-indigo-800">
                    <span className="animate-spin h-4 w-4 border-2 border-indigo-600 border-t-transparent rounded-full shrink-0" />
                    {t('events.replan_loading')}
                  </div>
                ) : null}

                {rowError.id === event.id && rowError.message ? (
                  <div className="mt-3 rounded-xl border border-red-200 bg-red-50 p-2.5 text-xs font-bold text-red-700 flex items-start justify-between gap-2">
                    <span>{rowError.message}</span>
                    <button type="button" onClick={() => setRowError({ id: null, message: '' })} aria-label={t('workout.close')}>
                      <HiXMark className="w-4 h-4" />
                    </button>
                  </div>
                ) : null}

                <div className="mt-3 flex flex-wrap gap-2">
                  {event.replan ? (
                    <button
                      type="button"
                      onClick={() => onOpenOptions?.(event)}
                      className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-bold transition-colors ${
                        status === 'pending' ? 'bg-indigo-600 text-white hover:bg-indigo-700' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                      }`}
                    >
                      <HiEye className="w-4 h-4" /> {t('events.view_options')}
                    </button>
                  ) : null}
                  {canEdit ? (
                    <>
                      <button
                        type="button"
                        onClick={() => handleReplan(event)}
                        disabled={Boolean(replanningId)}
                        className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-bold bg-white border border-indigo-100 text-indigo-700 hover:bg-indigo-50 disabled:opacity-50 disabled:cursor-not-allowed"
                      >
                        <HiArrowPath className={`w-4 h-4 ${isReplanning ? 'animate-spin' : ''}`} /> {t('events.replan')}
                      </button>
                      <button
                        type="button"
                        onClick={() => { setDeleteError(''); setDeleting(event); }}
                        disabled={isReplanning}
                        className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-bold bg-white border border-red-100 text-red-600 hover:bg-red-50 disabled:opacity-50"
                      >
                        <HiTrash className="w-4 h-4" /> {t('events.delete')}
                      </button>
                    </>
                  ) : null}
                </div>
              </div>
            );
          })}
        </div>
      )}

      {deleting ? (
        <DeleteEventDialog
          event={deleting}
          busy={deleteBusy}
          error={deleteError}
          onCancel={() => { if (!deleteBusy) setDeleting(null); }}
          onConfirm={handleDelete}
        />
      ) : null}
    </div>
  );
}
